import React, { useState } from "react";
import { Download, Loader2, Check, AlertCircle } from "lucide-react";

/**
 * DownloadBrochureButton — reusable brochure-download CTA with real fetch
 * + blob trigger and a visible loader during the 16 MB download.
 *
 * Variants:
 *   - "primary"  (default) : bold navy-on-brass CTA, used on catalogue page
 *   - "ghost"               : cream pill, used on hero/inquiry sections
 *   - "inline"              : underlined text link, used in footers / nav rails
 *
 * Mounts a real <a download> fallback for old browsers, but normally
 * intercepts the click and runs fetch() so we can show a spinner while
 * the 16 MB PDF transfers.
 */

const PDF_URL = "/vilora-impex-brochure.pdf";
const FILENAME = "Vilora-Impex-Brochure.pdf";

export default function DownloadBrochureButton({
  variant = "primary",
  label = "Download Brochure",
  shortLabel,
  className = "",
}) {
  const [state, setState] = useState("idle"); // idle | loading | done | error

  const onClick = async (e) => {
    if (state === "loading") {
      e.preventDefault();
      return;
    }
    // If user has fetch + Blob support, intercept and show a spinner.
    if (typeof fetch !== "function" || typeof URL.createObjectURL !== "function") {
      return; // let the native <a download> handle it
    }
    e.preventDefault();
    setState("loading");
    try {
      const res = await fetch(PDF_URL, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = FILENAME;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
      setState("done");
      window.setTimeout(() => setState("idle"), 2500);
    } catch (err) {
      console.warn("brochure download failed:", err);
      setState("error");
      window.setTimeout(() => setState("idle"), 3000);
    }
  };

  const stateLabel = (() => {
    if (state === "loading") return "Preparing PDF…";
    if (state === "done") return "Downloaded ✓";
    if (state === "error") return "Try again";
    return shortLabel || label;
  })();

  const Icon = (() => {
    if (state === "loading") return <Loader2 size={15} strokeWidth={1.8} className="dlb-spin" />;
    if (state === "done") return <Check size={15} strokeWidth={2} />;
    if (state === "error") return <AlertCircle size={15} strokeWidth={1.8} />;
    return <Download size={15} strokeWidth={1.8} />;
  })();

  const variantClass = `dlb dlb--${variant} ${state !== "idle" ? `dlb--${state}` : ""} ${className}`.trim();

  return (
    <a
      href={PDF_URL}
      download={FILENAME}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={variantClass}
      aria-label={state === "loading" ? "Downloading brochure" : "Download brochure PDF"}
      aria-busy={state === "loading"}
    >
      {variant !== "inline" ? (
        <span className="dlb-code">{state === "loading" ? "FETCHING" : "PDF"}</span>
      ) : null}
      <span className="dlb-label">{stateLabel}</span>
      <span className="dlb-icon" aria-hidden="true">{Icon}</span>
    </a>
  );
}
